// DSC35-S6 (DEC-257, DEC-258): the ONE place a discount schedule becomes
// words. Pure, with no database and no pg import, so the browser editor
// (DSC35-S5) can import the same function and the list and the editor can
// never word the same schedule two ways.
//
// Wording follows design/mockups/dsc35-list-*.png:
//   "Weekdays, 4:00 PM to 6:00 PM"
//   "Fridays and Saturdays, 10:00 PM to 2:00 AM (ends the next morning)"
//   "Every day, June 1 to August 31, every year"
// Parts are joined with ", " in this order: days, dates, time window. No
// time range adds no time phrase (the window is the whole day).

/** The schedule shape the formatter reads. Structurally the same as
 * DiscountSchedule in discountSchedule.ts and DiscountScheduleDto in the
 * browser api; declared here so this file imports nothing. */
export interface ScheduleSummaryInput {
  weekdays: readonly number[];
  dateFrom: string | null;
  dateTo: string | null;
  repeatYearly: boolean;
  timeFrom: string | null;
  timeTo: string | null;
}

const DAY_PLURALS = [
  "Mondays",
  "Tuesdays",
  "Wednesdays",
  "Thursdays",
  "Fridays",
  "Saturdays",
  "Sundays",
] as const;

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

export const DAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

/** "16:00" or "16:00:00" as "4:00 PM". Midnight is "12:00 AM". */
export function formatClockTime(time: string): string {
  const [h, m] = time.split(":");
  const hour = Number(h);
  const suffix = hour >= 12 ? "PM" : "AM";
  const twelve = hour % 12 === 0 ? 12 : hour % 12;
  return `${twelve}:${m ?? "00"} ${suffix}`;
}

function joinWithAnd(words: readonly string[]): string {
  if (words.length <= 1) {
    return words.join("");
  }
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

function dayPhrase(weekdays: readonly number[]): string {
  const days = [...new Set(weekdays)].sort((a, b) => a - b);
  const key = days.join(",");
  if (key === "1,2,3,4,5,6,7") {
    return "Every day";
  }
  if (key === "1,2,3,4,5") {
    return "Weekdays";
  }
  if (key === "6,7") {
    return "Weekends";
  }
  return joinWithAnd(days.map((d) => DAY_PLURALS[d - 1] ?? ""));
}

function datePart(iso: string): { year: number; month: number; day: number } {
  const [y, m, d] = iso.split("-").map(Number);
  return { year: y as number, month: m as number, day: d as number };
}

/** "June 1" for a yearly end, "June 1, 2026" for a dated one. */
function longDate(iso: string, withYear: boolean): string {
  const { year, month, day } = datePart(iso);
  const base = `${MONTH_NAMES[month - 1]} ${day}`;
  return withYear ? `${base}, ${year}` : base;
}

/** "Sep 30, 2026": the short form used by the list status, never a schedule. */
export function formatShortDate(iso: string, withYear: boolean): string {
  const { year, month, day } = datePart(iso);
  const base = `${MONTH_SHORT[month - 1]} ${day}`;
  return withYear ? `${base}, ${year}` : base;
}

function datePhrase(schedule: ScheduleSummaryInput): string | null {
  const { dateFrom, dateTo } = schedule;
  if (dateFrom === null || dateTo === null) {
    return null;
  }
  if (schedule.repeatYearly) {
    const range =
      dateFrom.slice(5) === dateTo.slice(5)
        ? longDate(dateFrom, false)
        : `${longDate(dateFrom, false)} to ${longDate(dateTo, false)}`;
    return `${range}, every year`;
  }
  if (dateFrom === dateTo) {
    return longDate(dateFrom, true);
  }
  const sameYear = dateFrom.slice(0, 4) === dateTo.slice(0, 4);
  return sameYear
    ? `${longDate(dateFrom, false)} to ${longDate(dateTo, true)}`
    : `${longDate(dateFrom, true)} to ${longDate(dateTo, true)}`;
}

function timePhrase(schedule: ScheduleSummaryInput): string | null {
  const { timeFrom, timeTo } = schedule;
  if (timeFrom === null || timeTo === null) {
    return null;
  }
  // A window ending exactly at midnight ends tonight, so it reads "to
  // midnight" and never "(ends the next morning)" (DSC35-S5).
  if (timeTo.slice(0, 5) === "00:00") {
    return `${formatClockTime(timeFrom)} to midnight`;
  }
  const range = `${formatClockTime(timeFrom)} to ${formatClockTime(timeTo)}`;
  // Overnight: the window belongs to the day it starts (DEC-258 (1)).
  return timeFrom > timeTo ? `${range} (ends the next morning)` : range;
}

export function summarizeSchedule(schedule: ScheduleSummaryInput): string {
  const parts = [dayPhrase(schedule.weekdays), datePhrase(schedule), timePhrase(schedule)];
  return parts.filter((part): part is string => part !== null).join(", ");
}

/** "Fri 10:00 PM" for a same-week opening, "Fri" for an all-day one, and
 * "Jun 1" when the opening is a week or more away. Inputs are the local
 * calendar date (YYYY-MM-DD), its ISO weekday, the local wall time of the
 * opening and whether the schedule has a time range, plus how many local
 * days away it is. Used by the server to label the list status. */
export function formatOpensLabel(params: {
  localDate: string;
  isoWeekday: number;
  localTime: string;
  hasTimeRange: boolean;
  daysAway: number;
  thisYear: number;
}): string {
  if (params.daysAway >= 7) {
    const sameYear = datePart(params.localDate).year === params.thisYear;
    return `Opens ${formatShortDate(params.localDate, !sameYear)}`;
  }
  const day = params.daysAway === 0 ? "today" : (DAY_SHORT[params.isoWeekday - 1] ?? "");
  return params.hasTimeRange ? `Opens ${day} ${formatClockTime(params.localTime)}` : `Opens ${day}`;
}
